import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import Button from '@/components/elements/Button';
import * as Dialog from '@/components/elements/Dialog';
import UIResource from '@/components/UIResource';
import { selectQuickFindLinkById } from '@/ducks/common/lookup/selectors';
import { resetFilters } from '@/ducks/filters/setters';
import { FILTER_KEY, type FiltersKey } from '@/ducks/filters/types';
import { updateSearchParams } from '@/ducks/routes/history';
import { useAppSelector } from '@/store';

import styles from './QuickFindWarningDialog.module.scss';

const QuickFindWarningDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  const quickFindId = searchParams.get(FILTER_KEY.quickFindID);
  const activeLink = useAppSelector((state) => selectQuickFindLinkById(state, quickFindId));

  useEffect(() => {
    if (activeLink) {
      const activeLinkParams = new URLSearchParams(activeLink?.linkUrl || '');

      const isChanged = activeLinkParams.keys().some((key) => {
        const searchValue = searchParams.get(key);
        const linkValue = activeLinkParams.get(key);
        return searchValue !== linkValue;
      });

      if (isChanged) {
        setIsOpen(true);
      }
    }
  }, [quickFindId, activeLink, searchParams]);

  if (!activeLink) {
    return null;
  }

  const agreeHandler = () => {
    const keys = [FILTER_KEY.quickFindID];
    resetFilters(keys as FiltersKey[]);
    setIsOpen(false);
  };

  const dismissHandler = () => {
    const activeLinkParams = new URLSearchParams(activeLink?.linkUrl ?? '');
    const params = new URLSearchParams(searchParams);
    activeLinkParams.forEach((value, key) => {
      if (params.get(key) !== value) {
        params.set(key, value);
      }
    });

    updateSearchParams({ searchParams: params });
    setIsOpen(false);
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      dismissHandler();
    }
    setIsOpen(open);
  };

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={isOpen}>
      <Dialog.Content
        className={styles.content}
        closeSlot={<Dialog.CloseButton aria-label="Close quick find warning dialog" className={styles.closeButton} />}
        containerClassName={styles.root}
        overlayClassName={styles.overlay}
      >
        <Dialog.Title className={styles.title}>
          <UIResource id="QuickFind.warningDialog.title" values={{ label: activeLink.linkTitle }} />
        </Dialog.Title>

        <Dialog.Description className={styles.description}>
          <UIResource id="QuickFind.warningDialog.description" />
        </Dialog.Description>

        <div className={styles.footer}>
          <Button className={styles.agreeButton} onClick={agreeHandler} variant="primary">
            <UIResource id="QuickFind.warningDialog.agreeButton" />
          </Button>

          <Button className={styles.dismissButton} onClick={dismissHandler} variant="secondary">
            <UIResource id="QuickFind.warningDialog.dismissButton" />
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default QuickFindWarningDialog;
